import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  AzureManagementServiceApiRef,
  AIFoundrySelector,
  AIProvisioningServiceApiRef,
  AIOneTrustServiceApiRef,
  NotificationServiceApiRef,
  validateOneTrust,
} from '@runway/devkit';
import type { AIFoundryMapping, AIFoundrySelectorOutput } from '@runway/devkit';
import { useAsync } from 'react-use';

/** Distribution list for AI production provisioning notifications */
const AI_NOTIFICATION_RECIPIENTS = ['DL_AI_LandingZone@aa.com'];
import { catalogProjectsServiceApiRef } from '../../services/catalogProjects.service';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    minWidth: 500,
    paddingTop: theme.spacing(2),
  },
  selectorContainer: {
    marginBottom: theme.spacing(2),
  },
  sectionTop: {
    marginTop: theme.spacing(2),
  },
  statusMessage: {
    marginTop: theme.spacing(2),
  },
}));

type ProvisioningStep = 'idle' | 'submitting' | 'success' | 'error';

interface ProdProvisioningDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
}

export const ProdProvisioningDialog = ({ isOpen, onClose }: ProdProvisioningDialogProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const amsApi = useApi(AzureManagementServiceApiRef);
  const catalogProjectsApi = useApi(catalogProjectsServiceApiRef);
  const provisioningService = useApi(AIProvisioningServiceApiRef);
  const oneTrustService = useApi(AIOneTrustServiceApiRef);
  const notificationService = useApi(NotificationServiceApiRef);

  const [selectedFoundry, setSelectedFoundry] = useState<AIFoundryMapping | null>(null);
  const [aadGroup, setAadGroup] = useState<string>('');
  const [step, setStep] = useState<ProvisioningStep>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const entityName = entity.metadata.name;
  const aiCategory = (entity as any).spec?.aiInfo?.aiCategory || '';

  // Resolve cmdbAppShortName: custom properties → aa.yaml (no fallback to entity name)
  const appShortnameState = useAsync(
    () => provisioningService.resolveAppShortname(catalogProjectsApi, entity, entityName),
    [provisioningService, catalogProjectsApi, entity, entityName],
  );

  const appShortname = appShortnameState.value || '';

  // Fetch the AI config YAML from GitHub
  const aiConfigState = useAsync(async () => {
    if (!isOpen || !appShortname || appShortnameState.loading || !aiCategory) return null;
    return provisioningService.fetchAIConfig(appShortname, aiCategory);
  }, [isOpen, appShortname, appShortnameState.loading, aiCategory, provisioningService]);

  // Fetch OneTrust approved app short names from GitHub
  const oneTrustPolicy = useAsync(async () => {
    if (!isOpen || !appShortname || appShortnameState.loading) return null;
    return oneTrustService.fetchOneTrustPolicy();
  }, [isOpen, appShortname, appShortnameState.loading, oneTrustService]);

  // Derive OneTrust approval status and error message in a single pass
  const { isApproved: isOneTrustApproved, errorMessage: oneTrustErrorMessage } = useMemo((): {
    isApproved: boolean;
    errorMessage: ReactNode | null;
  } => {
    if (oneTrustPolicy.loading) {
      return { isApproved: false, errorMessage: null };
    }
    if (oneTrustPolicy.error) {
      return {
        isApproved: false,
        errorMessage: `Failed to fetch OneTrust approved apps policy: ${oneTrustPolicy.error.message}`,
      };
    }
    if (!oneTrustPolicy.value) {
      return { isApproved: false, errorMessage: null };
    }
    if (validateOneTrust(oneTrustPolicy.value, appShortname)) {
      return { isApproved: true, errorMessage: null };
    }
    return {
      isApproved: false,
      errorMessage: (
        <>
          The application &quot;{appShortname}&quot; has not been approved/assessed by OneTrust for AI resource
          provisioning in production. Please go through the{' '}
          <Link
            href="https://jetnet.aa.com/sites/gen-ai/SitePageModern/321926/genai-overview"
            target="_blank"
            rel="noopener noreferrer"
          >
            GenAI Onboarding Process
          </Link>{' '}
          as a prerequisite.
        </>
      ),
    };
  }, [oneTrustPolicy.loading, oneTrustPolicy.error, oneTrustPolicy.value, appShortname]);
  /** Handles both foundry and AAD group selection changes from AIFoundrySelector */
  const handleSelectionChange = useCallback((selection: AIFoundrySelectorOutput) => {
    setSelectedFoundry(selection.foundry);
    setAadGroup(selection.aadGroup);
  }, []);

  /** Creates the production AI Foundry project via the provisioning service */
  const handleSubmit = useCallback(async () => {
    if (!selectedFoundry || !aadGroup) return;

    setStep('submitting');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await provisioningService.provisionProdResources(
        { entityName, appShortname, aiCategory, foundry: selectedFoundry, aadGroup },
        amsApi,
      );
      setStep('success');
      setSuccessMessage(
        `Production AI Foundry project "${entityName}" created successfully. APIM configuration and hidden layer setup triggered.`,
      );

      // Send email notification (fire-and-forget — do not block on failure)
      try {
        const recipients = AI_NOTIFICATION_RECIPIENTS;
        const subject = `AI Agent Promoted to Prod: ${entityName}`;
        const body = `
          <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse;">
            <tr><td><strong>Application Name (Repo)</strong></td><td>${entityName}</td></tr>
            <tr><td><strong>App Shortname</strong></td><td>${appShortname}</td></tr>
            <tr><td><strong>AI Use Case</strong></td><td>${aiCategory}</td></tr>
          </table>
          <p>This is an automated notification from Runway.</p>
        `;
        await notificationService.sendEmail(recipients, body, subject);
      } catch {
        // Email failure is non-blocking
      }
    } catch (error) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }, [
    selectedFoundry,
    aadGroup,
    amsApi,
    entityName,
    appShortname,
    aiCategory,
    provisioningService,
    notificationService,
  ]);

  /** Resets state and closes the dialog (blocked while submitting) */
  const handleClose = useCallback(() => {
    if (step !== 'submitting') {
      setStep('idle');
      setSelectedFoundry(null);
      setAadGroup('');
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  }, [step, onClose]);

  const isSubmitDisabled =
    !selectedFoundry ||
    !aadGroup ||
    !appShortname ||
    step === 'submitting' ||
    appShortnameState.loading ||
    oneTrustPolicy.loading ||
    aiConfigState.loading ||
    !isOneTrustApproved;

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      aria-labelledby="prod-provisioning-dialog-title"
    >
      <DialogTitle id="prod-provisioning-dialog-title">Provision AI Resources in Production</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {(step === 'submitting' || appShortnameState.loading || oneTrustPolicy.loading || aiConfigState.loading) && (
          <LinearProgress aria-label="Provisioning in progress" />
        )}

        {/* App short name resolution error */}
        {appShortnameState.error && (
          <Alert severity="error" className={classes.statusMessage}>
            {appShortnameState.error.message}
          </Alert>
        )}

        {/* OneTrust validation error - shown instead of form */}
        {!oneTrustPolicy.loading && oneTrustErrorMessage && (
          <Alert severity="error" className={classes.statusMessage}>
            {oneTrustErrorMessage}
          </Alert>
        )}

        {/* Show selector only when validation passes */}
        {!appShortnameState.loading && !appShortnameState.error && !oneTrustPolicy.loading && isOneTrustApproved && (
          <>
            {aiConfigState.loading && (
              <Typography variant="body2" gutterBottom className={classes.sectionTop}>
                Loading AI configuration...
              </Typography>
            )}

            {aiConfigState.error && (
              <Alert severity="warning" className={classes.statusMessage}>
                Could not load AI configuration for vertical filtering. Showing all foundries.
              </Alert>
            )}

            {!aiConfigState.loading && (
              <div className={classes.selectorContainer}>
                <AIFoundrySelector
                  onChange={handleSelectionChange}
                  label="Select Production AI Foundry Instance"
                  disabled={step === 'submitting'}
                  environment="prod"
                  aiConfigData={aiConfigState.value ?? null}
                />
              </div>
            )}
          </>
        )}

        {step === 'error' && (
          <Alert severity="error" className={classes.statusMessage}>
            {errorMessage}
          </Alert>
        )}

        {step === 'success' && (
          <Alert severity="success" className={classes.statusMessage}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={step === 'submitting'}>
          {step === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {step !== 'success' && isOneTrustApproved && appShortname && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitDisabled}
            data-testid="prod-provisioning-submit"
          >
            {step === 'submitting' ? 'Provisioning...' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
